import { useState, type Ref } from "react";
import { Plus, Upload } from "lucide-react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { useNumberFormat } from "@/shared/hooks/use-number-format";
import { formatAmountScale } from "@/shared/lib/amount";
import { formatDate, timeOfDayOrNull } from "@/shared/lib/date-utils";
import { labelOr } from "@/shared/lib/label";
import { nextSortDirection } from "@/shared/lib/sort";
import { Checkbox } from "@/shared/ui/checkbox";
import type { ExternalTrade, ExternalTradeOrder } from "@/features/portfolio/api/external-trade";
import { tradeStatusBadgeVariant } from "@/features/portfolio/lib/trade-status";
import { AddTradeDialog } from "./AddTradeDialog";

interface OperationsTabProps {
  portfolioId: string;
  portfolioStatus?: string;
  trades: ExternalTrade[];
  isLoading: boolean;
  isReloading: boolean;
  isTruncated: boolean;
  order: ExternalTradeOrder;
  onOrderChange: (order: ExternalTradeOrder) => void;
  showsCanceled: boolean;
  onShowsCanceledChange: (showsCanceled: boolean) => void;
  onTradesRecorded: () => void;
  hasFailed: boolean;
  onRetry: () => void;
  /** True while a CSV import (of either type) is open on this portfolio — see
   *  `useCsvImport`. Blocks opening a second import from either entry point. */
  isImportOpen: boolean;
  /** Called with the clicked button itself, so `useCsvImport` can hand focus back to it
   *  once the panel closes — see `openImport` there. */
  onOpenImport: (trigger: HTMLElement) => void;
  /** Receives the import entry button, so `useCsvImport` can focus it on a Close after
   *  the button that opened the panel unmounted — see `activeEntryButton` there. */
  importButtonRef?: Ref<HTMLButtonElement>;
}

type SortableColumn = Exclude<ExternalTradeOrder, `-${string}`>;

const SIDE_VARIANT: Record<string, "default" | "secondary"> = {
  buy: "default",
  sell: "secondary",
};

const SortableHead = ({
  column,
  label,
  order,
  isReloading,
  onOrderChange,
}: {
  column: SortableColumn;
  label: string;
  order: ExternalTradeOrder;
  isReloading: boolean;
  onOrderChange: (order: ExternalTradeOrder) => void;
}) => {
  const isSorted = order === column || order === `-${column}`;
  const isDescending = order === `-${column}`;
  const direction = !isSorted ? null : isDescending ? "descending" : "ascending";

  return (
    <SortableTableHead
      label={label}
      direction={direction}
      // The toggle rule is `nextSortDirection`'s, shared with the positions table; this
      // line is only the adapter onto the `-` prefix the blotter sends over the wire.
      onSort={() => onOrderChange(
        nextSortDirection(direction) === "descending" ? `-${column}` : column,
      )}
      testId={`blotter-sort-${column}`}
      disabled={isReloading}
    />
  );
};

export const OperationsTab = ({
  portfolioId,
  portfolioStatus,
  trades,
  isLoading,
  isReloading,
  isTruncated,
  order,
  onOrderChange,
  showsCanceled,
  onShowsCanceledChange,
  onTradesRecorded,
  hasFailed,
  onRetry,
  isImportOpen,
  onOpenImport,
  importButtonRef,
}: OperationsTabProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();
  const { numberFormat } = useNumberFormat();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const acceptsTrades = portfolioStatus === "active";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <label className="mr-auto flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={showsCanceled}
            onCheckedChange={(checked) => onShowsCanceledChange(checked === true)}
            disabled={isLoading || isReloading}
            data-testid="blotter-shows-canceled"
          />
          {t("portfolio.showCanceled")}
        </label>
        {portfolioStatus && !acceptsTrades && (
          <p className="text-sm text-muted-foreground" data-testid="portfolio-trades-blocked">
            {t("portfolio.tradesBlocked")}
          </p>
        )}
        <Button
          ref={importButtonRef}
          variant="outline"
          // A disabled native button drops focus to <body> the instant this very click
          // disables it. `aria-disabled` plus a no-op keeps it focusable instead — the
          // panel that opens right after moves focus into itself. b-cell-position refuses
          // a bulk trade import on a non-active portfolio exactly like it refuses one
          // typed by hand (`portfolio-trades-blocked` above already names that reason);
          // an import already open needs no separate reason now that the panel — always
          // rendered above the tabs — is visibly the thing blocking a second one.
          aria-disabled={isImportOpen || !acceptsTrades}
          onClick={(event) => {
            if (isImportOpen || !acceptsTrades) return;
            onOpenImport(event.currentTarget);
          }}
          className={isImportOpen || !acceptsTrades ? "opacity-50" : undefined}
          data-testid="import-open-external-trade"
        >
          <Upload className="w-4 h-4 mr-2" />
          {t("portfolio.import.open.trade-parse-request")}
        </Button>
        <Button
          onClick={() => setShowAddDialog(true)}
          data-testid="add-trade"
          disabled={isLoading || !acceptsTrades}
          aria-busy={isLoading}
          title={portfolioStatus && !acceptsTrades ? t("portfolio.tradesBlocked") : undefined}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("portfolio.addTrade")}
        </Button>
      </div>

      {hasFailed && (
        <Card
          className="flex flex-wrap items-center justify-between gap-3 border-destructive p-4"
          data-testid="portfolio-operations-unavailable"
        >
          <p className="text-destructive">{t("portfolio.operationsUnavailable")}</p>
          <Button variant="outline" size="sm" onClick={onRetry} data-testid="portfolio-operations-retry">
            {t("portfolio.retry")}
          </Button>
        </Card>
      )}

      {isLoading && (
        <Card className="p-6">
          <div className="skeleton-shimmer h-4 w-full rounded bg-muted mb-3" />
          <div className="skeleton-shimmer h-4 w-5/6 rounded bg-muted" />
        </Card>
      )}

      {!isLoading && !hasFailed && trades.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground" data-testid="blotter-empty">
          {showsCanceled ? t("portfolio.tradesEmpty") : t("portfolio.tradesEmptyFiltered")}
        </Card>
      )}

      {!isLoading && trades.length > 0 && (
        <Card
          aria-busy={isReloading}
          className={isReloading ? "opacity-60 transition-opacity" : "transition-opacity"}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead
                  column="reference_time"
                  label={t("portfolio.col.referenceTime")}
                  order={order}
                  isReloading={isReloading}
                  onOrderChange={onOrderChange}
                />
                <SortableHead
                  column="created"
                  label={t("portfolio.col.created")}
                  order={order}
                  isReloading={isReloading}
                  onOrderChange={onOrderChange}
                />
                <TableHead>{t("portfolio.col.instrument")}</TableHead>
                <TableHead>{t("portfolio.col.side")}</TableHead>
                <TableHead className="text-right">{t("portfolio.col.quantity")}</TableHead>
                <TableHead className="text-right">{t("portfolio.col.price")}</TableHead>
                <TableHead>{t("portfolio.col.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trades.map((trade) => (
                <TableRow key={trade.id} data-testid="blotter-row">
                  <TableCell>
                    {formatDate(trade.reference_time, dateFormat)}
                    {timeOfDayOrNull(trade.reference_time) && (
                      <span className="ml-2 text-muted-foreground tabular-nums">
                        {timeOfDayOrNull(trade.reference_time)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground" title={trade.created}>
                    {formatDate(trade.created, dateFormat)}
                  </TableCell>
                  <TableCell className="font-medium">{trade.symbol || trade.instrument_id}</TableCell>
                  <TableCell>
                    <Badge variant={SIDE_VARIANT[trade.side] || "secondary"}>
                      {labelOr(t, `portfolio.side.${trade.side}`, trade.side)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatAmountScale(trade.symbol_amount, trade.symbol_scale, numberFormat, { trimZeros: true })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatAmountScale(trade.price_amount, trade.price_scale, numberFormat)} {trade.currency}
                  </TableCell>
                  <TableCell>
                    <Badge variant={tradeStatusBadgeVariant(trade.status)} data-testid="blotter-status">
                      {labelOr(t, `status.${trade.status}`, trade.status)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {!isLoading && isTruncated && (
        <p className="text-sm text-muted-foreground" data-testid="blotter-truncated">
          {t("portfolio.truncatedBlotter")}
        </p>
      )}

      <AddTradeDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        portfolioId={portfolioId}
        onRecorded={() => {
          setShowAddDialog(false);
          onTradesRecorded();
        }}
      />
    </div>
  );
};
